import React, { useState, useEffect, useRef } from "react";
import PlayIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import SettingsIcon from "@material-ui/icons/Settings";

import { PresenterClock } from "./PresenterClock";
import { GTag } from "../../utilities/Tracking/GoogleAnalytics";
import { ToolbarLocation, TimeMode } from "../../entities/PresenterPreferences";
import { usePrevious } from "../../utilities/UsePreviousHook";
import { Grid, Paper, IconButton, Typography, LinearProgress, makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
	presenterToolBarWrapper: {
		position: "fixed",
		width: "400px",
		marginLeft: "-200px",
		left: "50%",
	},
	presenterToolBarBottom: {
		bottom: 0,
	},
	presenterToolBarTop: {
		top: 0,
	},
	presenterToolBar: {
		[theme.breakpoints.up("sm")]: {
			margin: theme.spacing(2),
		},
		backgroundColor: "rgba(0, 0, 0, 0.85)",
		color: "white",
	},
	presenterToolBarDetails: {
		padding: 5,
	},
	timeDisplay: {
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
	},
	timerProgressBar: {
		backgroundColor: "rgba(0, 0, 0, 0.0)",
	}
}));

export interface Props{
	barLocation: ToolbarLocation;
	timeMode: string;
	timeLimit: number;
	toggleDrawer: () => void;
}

export interface State {
	timeMode: string;
	timeLimit: number;
	timerStartSecond: number;
	timerStartTime?: Date;
	timerDisplaySecond: number;
	timerProgressPercent: number;
}

export function PresenterToolBar(props: Props) {
	const [timeMode, setTimeMode] = useState(props.timeMode);
	const [timeLimit, setTimeLimit] = useState(props.timeLimit);
	const [timerStartSecond, setTimerStartSecond] = useState(props.timeLimit * 60);
	const [timerDisplaySecond, setTimerDisplaySecond] = useState(props.timeLimit * 60);
	const [timerProgressPercent, setTimerProgressPercent] = useState(0);
	const [isTimerOn, setIsTimerOn] = useState(false);

	const prevTimeMode = usePrevious(timeMode);
	const prevTimeLimit = usePrevious(timeLimit);

	const timerIntervalRef = useRef<NodeJS.Timeout | undefined>();
	const timerStartTimeRef = useRef<Date>();

	const classes = useStyles();
	const barStyling = `${classes.presenterToolBarWrapper} ${
		props.barLocation === ToolbarLocation.Top ? classes.presenterToolBarTop : classes.presenterToolBarBottom
	}`;

	useEffect(() => {
		if (props.timeMode !== timeMode || (props.timeMode === TimeMode.CountDown && props.timeLimit !== timeLimit)) {
			setTimeMode(props.timeMode);
			setTimeLimit(props.timeLimit);
		}
	}, [props.timeMode, timeMode, props.timeLimit, timeLimit]);

	useEffect(() => {
		if (prevTimeMode !== props.timeMode || (prevTimeMode === TimeMode.CountDown && prevTimeLimit !== props.timeLimit)) {
			updateSettings(props.timeMode, props.timeLimit);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prevTimeLimit, prevTimeMode, props.timeLimit, props.timeMode]);

	const onStartTimer = () => {
		setIsTimerOn(true);
		clearInterval(timerIntervalRef.current!);
		timerStartTimeRef.current = new Date();

		const intervalId = setInterval(() => {
			const timeElapsedInSeconds = calculateTimerElapsedDurationInSeconds();
			const displaySeconds =
				timeMode === TimeMode.CountDown ? timerStartSecond - timeElapsedInSeconds : timerStartSecond + timeElapsedInSeconds;
			const progressPercent = timeMode === TimeMode.CountDown ? getTimerProgressPercent(timeLimit, displaySeconds) : 0;
			setTimerDisplaySecond(displaySeconds);
			setTimerProgressPercent(progressPercent);
		}, 1000);
		timerIntervalRef.current = intervalId;
		GTag.event("Timer Interaction", { event_category: "Presenter", event_label: "Started" });
	};

	const onPauseTimer = () => {
		setIsTimerOn(false);
		GTag.event("Timer Interaction", { event_category: "Presenter", event_label: "Paused" });
		clearInterval(timerIntervalRef.current!);
		setTimerStartSecond(timerDisplaySecond);
	};

	const calculateTimerElapsedDurationInSeconds = (): number => {
		if (timerStartTimeRef.current !== undefined) {
			return Math.trunc(Math.abs((new Date().getTime() - timerStartTimeRef.current.getTime()) / 1000));
		} else {
			return 0;
		}
	};

	const getTimerProgressPercent = (timeLimit: number, timerDisplaySecond: number) => {
		return Math.ceil(((timeLimit * 60 - timerDisplaySecond) / (timeLimit * 60)) * 100 * 100) / 100;
	};

	const updateSettings = (timeMode: string, timeLimit: number) => {
		onPauseTimer();
		setTimeMode(timeMode);
		setTimeLimit(timeLimit);
		setTimerStartSecond(timeMode === TimeMode.CountDown ? timeLimit * 60 : 0);
		setTimerDisplaySecond(timeMode === TimeMode.CountDown ? timeLimit * 60 : 0);
		setTimerProgressPercent(0);
	};

	const displayTimer = (seconds: number): string => {
		let isNegative: boolean = seconds < 0;
		let sec = Math.abs(seconds);
		const minutes = Math.floor(sec / 60);
		const remainderSeconds = sec % 60;
		const displayMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
		const displaySeconds = remainderSeconds < 10 ? `0${remainderSeconds}` : `${remainderSeconds}`;

		return `${isNegative ? "-" : ""}${displayMinutes}:${displaySeconds}`;
	};

	return (
		<Grid container direction="column" justifyContent="center" alignItems="center" className={barStyling}>
			<Paper className={classes.presenterToolBar} elevation={2}>
				<Grid container direction="row" justifyContent="center" alignItems="center" className={classes.presenterToolBarDetails}>
					<IconButton
						color="inherit"
						onClick={() => {
							isTimerOn ? onPauseTimer() : onStartTimer();
						}}
						disabled={timeMode === TimeMode.Clock}
					>
						{isTimerOn ? <PauseIcon /> : <PlayIcon />}
					</IconButton>
					<div className={classes.timeDisplay}>
						{timeMode !== TimeMode.Clock && (
							<Typography variant="h3" color="inherit">
								{displayTimer(timerDisplaySecond)}
							</Typography>
						)}
						{timeMode === TimeMode.Clock && <PresenterClock />}
					</div>
					<IconButton color="inherit" onClick={props.toggleDrawer}>
						<SettingsIcon />
					</IconButton>
				</Grid>
				{timeMode === TimeMode.CountDown && (
					<LinearProgress color="primary" variant="determinate" value={timerProgressPercent} className={classes.timerProgressBar} />
				)}
			</Paper>
		</Grid>
	);
}
